import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Database,
  FileText,
  TrendingUp,
  BarChart3,
  BookOpen,
  Check,
  X,
  Search,
  ArrowDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ─── Per-tab animated illustrations ─── */

// Mirrors the real Kreator Scenariuszy flow: type a topic into the input,
// tap the generate button, watch it work, get the result.
const CREATOR_TOPIC = "Jak zdobyć 10 tys. wyświetleń w tydzień";
const CREATOR_CHAR_MS = 35;

const CreatorAnimation = () => {
  const [typedChars, setTypedChars] = useState(0);
  const [pressed, setPressed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let charTimer: ReturnType<typeof setInterval>;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const runCycle = () => {
      setTypedChars(0);
      setPressed(false);
      setLoading(false);
      setDone(false);

      let i = 0;
      charTimer = setInterval(() => {
        i += 1;
        setTypedChars(i);
        if (i >= CREATOR_TOPIC.length) clearInterval(charTimer);
      }, CREATOR_CHAR_MS);

      const typingDone = CREATOR_TOPIC.length * CREATOR_CHAR_MS + 350;
      timeouts.push(
        setTimeout(() => setPressed(true), typingDone),
        setTimeout(() => { setPressed(false); setLoading(true); }, typingDone + 250),
        setTimeout(() => { setLoading(false); setDone(true); }, typingDone + 1450),
        setTimeout(runCycle, typingDone + 2650),
      );
    };

    runCycle();
    return () => {
      clearInterval(charTimer);
      timeouts.forEach(clearTimeout);
    };
  }, []);

  const typedText = CREATOR_TOPIC.slice(0, typedChars);
  const isTyping = typedChars < CREATOR_TOPIC.length;

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-2 px-3">
      {/* Topic input — mirrors the real kreator's "O czym chcesz dzisiaj nagrać?" textarea */}
      <div className="w-full min-h-[44px] rounded-xl bg-white border-2 border-primary/30 px-3 py-2.5 shadow-sm flex items-start">
        <p className="text-[11px] md:text-xs text-gray-700 leading-snug text-left">
          {typedText}
          {isTyping && (
            <motion.span
              className="inline-block w-[2px] h-3 bg-primary ml-0.5 align-middle"
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.7, repeat: Infinity }}
            />
          )}
        </p>
      </div>

      {/* Generate button — mirrors the real "Przygotuj virala" CTA */}
      <motion.div
        animate={{ scale: pressed ? 0.94 : 1 }}
        transition={{ duration: 0.15 }}
        className="w-full rounded-full bg-primary text-white text-[11px] md:text-xs font-bold py-2 flex items-center justify-center gap-1.5 shadow-md shadow-primary/25"
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.span
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1.5"
            >
              <motion.span
                className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full inline-block"
                animate={{ rotate: 360 }}
                transition={{ duration: 0.7, repeat: Infinity, ease: "linear" }}
              />
              Tworzę scenariusz...
            </motion.span>
          ) : done ? (
            <motion.span
              key="done"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1.5"
            >
              <Check className="w-3.5 h-3.5" />
              Gotowe!
            </motion.span>
          ) : (
            <motion.span
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1.5"
            >
              🎬 Przygotuj virala
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

const InspirationAnimation = () => {
  const items = [
    { emoji: "🎥", startX: -55, startY: -30 },
    { emoji: "📸", startX: 50, startY: -35 },
    { emoji: "💡", startX: -45, startY: 30 },
    { emoji: "🔥", startX: 55, startY: 25 },
    { emoji: "✨", startX: 0, startY: -50 },
  ];

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      {/* Orbiting content pieces getting pulled in */}
      {items.map((item, i) => (
        <motion.div
          key={i}
          className="absolute text-lg"
          animate={{
            x: [item.startX, item.startX * 0.3, 0],
            y: [item.startY, item.startY * 0.3, 0],
            scale: [1, 0.8, 0],
            opacity: [1, 0.8, 0],
          }}
          transition={{
            duration: 2.2,
            repeat: Infinity,
            delay: i * 0.5,
            ease: "easeIn",
          }}
        >
          {item.emoji}
        </motion.div>
      ))}

      {/* Central magnet */}
      <motion.div
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        className="relative z-10 text-4xl"
        style={{ filter: "drop-shadow(0 4px 12px rgba(234,88,12,0.25))" }}
      >
        🧲
      </motion.div>

      {/* Magnetic field rings */}
      {[0, 1].map((i) => (
        <motion.div
          key={`ring-${i}`}
          className="absolute rounded-full border border-orange-300/30"
          style={{ width: 70 + i * 30, height: 70 + i * 30 }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.15, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.5 }}
        />
      ))}
    </div>
  );
};

const LibraryAnimation = () => {
  const books = [
    { color: "#F97316", width: 14, label: "🔥" },
    { color: "#3B82F6", width: 16, label: "💡" },
    { color: "#EF4444", width: 13, label: "🎯" },
    { color: "#8B5CF6", width: 15, label: "⭐" },
    { color: "#10B981", width: 14, label: "📝" },
    { color: "#F59E0B", width: 16, label: "🚀" },
    { color: "#EC4899", width: 13, label: "✨" },
  ];

  const pullIndex = 3; // book that gets pulled out

  return (
    <div className="relative w-full h-full flex items-end justify-center pb-6">
      {/* Shelf */}
      <div className="absolute bottom-4 left-[15%] right-[15%] h-2 rounded-full bg-orange-200/60" />
      <div className="absolute bottom-3 left-[14%] right-[14%] h-1 rounded-full bg-orange-300/30" />

      {/* Books row */}
      <div className="relative flex items-end gap-[3px]" style={{ height: "75%" }}>
        {books.map((book, i) => {
          const isPulled = i === pullIndex;
          const height = 70 + (i % 3) * 10;

          return (
            <motion.div
              key={i}
              className="relative rounded-t-sm cursor-pointer"
              style={{
                width: book.width,
                height: `${height}%`,
                background: `linear-gradient(180deg, ${book.color}dd 0%, ${book.color} 100%)`,
                boxShadow: `inset -2px 0 3px rgba(0,0,0,0.15), 2px 0 4px rgba(0,0,0,0.08)`,
                borderRadius: "2px 2px 0 0",
              }}
              initial={{ y: 0 }}
              animate={isPulled ? {
                y: [0, -18, -18, 0],
                rotateZ: [0, -2, -2, 0],
              } : { y: 0 }}
              transition={isPulled ? {
                duration: 3,
                repeat: Infinity,
                repeatDelay: 2,
                times: [0, 0.2, 0.7, 1],
                ease: "easeInOut",
              } : undefined}
            >
              {/* Book spine detail */}
              <div
                className="absolute top-2 left-1/2 -translate-x-1/2 text-[7px]"
                style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
              >
                {book.label}
              </div>
              <div
                className="absolute inset-x-0 top-[20%] h-[1px] mx-[2px]"
                style={{ background: `rgba(255,255,255,0.3)` }}
              />
              <div
                className="absolute inset-x-0 bottom-[15%] h-[1px] mx-[2px]"
                style={{ background: `rgba(255,255,255,0.3)` }}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const CompetitionAnimation = () => (
  <div className="relative w-full h-full flex items-end justify-center gap-3 px-6 pb-6">
    {/* Competitor 1 - smallest */}
    <motion.div
      className="flex flex-col items-center gap-1.5"
      animate={{ y: [0, 3, 0] }}
      transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
    >
      <div className="w-9 h-9 rounded-full bg-gray-200 border-2 border-gray-300/60 flex items-center justify-center text-sm opacity-60">
        👤
      </div>
      <div className="text-[10px] font-bold text-gray-300 tabular-nums">8.2K</div>
      <motion.div
        className="w-6 rounded-t-md bg-gradient-to-t from-gray-300 to-gray-200"
        initial={{ height: 0 }}
        animate={{ height: 32 }}
        transition={{ delay: 0.2, duration: 0.8, type: "spring" }}
      />
    </motion.div>

    {/* Competitor 2 - medium */}
    <motion.div
      className="flex flex-col items-center gap-1.5"
      animate={{ y: [0, 2, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
    >
      <div className="w-10 h-10 rounded-full bg-gray-200 border-2 border-gray-300/70 flex items-center justify-center text-base opacity-70">
        👤
      </div>
      <div className="text-[10px] font-bold text-gray-400 tabular-nums">12.4K</div>
      <motion.div
        className="w-7 rounded-t-md bg-gradient-to-t from-gray-400 to-gray-300"
        initial={{ height: 0 }}
        animate={{ height: 52 }}
        transition={{ delay: 0.4, duration: 0.8, type: "spring" }}
      />
    </motion.div>

    {/* YOU - tallest, highlighted */}
    <motion.div
      className="flex flex-col items-center gap-1.5"
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    >
      <motion.div
        className="w-13 h-13 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-2 border-orange-300 flex items-center justify-center text-xl shadow-lg"
        style={{ width: 52, height: 52 }}
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        🦊
      </motion.div>
      <div className="text-[11px] font-extrabold text-orange-600 tabular-nums">24.1K</div>
      <motion.div
        className="w-8 rounded-t-md bg-gradient-to-t from-orange-500 to-orange-400 relative"
        initial={{ height: 0 }}
        animate={{ height: 80 }}
        transition={{ delay: 0.6, duration: 0.8, type: "spring" }}
      />
    </motion.div>
  </div>
);

const AnalyticsAnimation = () => {
  // Big dot = post moment, then chart jumps UP from it, flat before
  const points = [
    { x: 8, y: 52, post: false },
    { x: 22, y: 50, post: false },
    { x: 30, y: 50, post: true },    // POST → jump starts here
    { x: 36, y: 38, post: false },   // jump up
    { x: 50, y: 37, post: false },   // flat
    { x: 58, y: 37, post: true },    // POST → jump starts here
    { x: 64, y: 22, post: false },   // jump up
    { x: 78, y: 21, post: false },   // flat
    { x: 86, y: 21, post: true },    // POST → jump starts here
    { x: 92, y: 10, post: false },   // jump up
    { x: 112, y: 8, post: false },   // end flat
  ];

  const pathD = "M 8 52 L 22 50 L 30 50 L 36 38 L 50 37 L 58 37 L 64 22 L 78 21 L 86 21 L 92 10 L 112 8";
  const areaD = pathD + " V 58 H 8 Z";

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      {/* Grid lines */}
      <svg viewBox="0 0 120 60" className="absolute inset-0 w-full h-full" fill="none">
        {[15, 30, 45].map((y) => (
          <line key={y} x1="4" y1={y} x2="116" y2={y} stroke="rgba(0,0,0,0.04)" strokeWidth="0.5" />
        ))}
        {[24, 52, 80, 112].map((x) => (
          <line key={x} x1={x} y1="4" x2={x} y2="56" stroke="rgba(0,0,0,0.03)" strokeWidth="0.5" />
        ))}
      </svg>

      <svg viewBox="0 0 120 60" className="w-full h-full relative z-10" fill="none">
        {/* Area fill */}
        <motion.path
          d={areaD}
          fill="url(#areaGrad2)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        />
        {/* Main line */}
        <motion.path
          d={pathD}
          stroke="url(#chartGrad2)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />

        {/* Only post dots */}
        {points.filter(p => p.post).map((p, i) => (
          <motion.circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={3.5}
            fill="#ea580c"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 + i * 0.3, duration: 0.3, type: "spring" }}
          />
        ))}

        {/* Reel emoji markers on post days */}
        {points.filter(p => p.post).map((p, i) => (
          <motion.g key={`reel-${i}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 + i * 0.2, duration: 0.4 }}
          >
            <text x={p.x} y={p.y - 6} textAnchor="middle" fontSize="6" dominantBaseline="auto">
              🎬
            </text>
          </motion.g>
        ))}

        <defs>
          <linearGradient id="chartGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#fdba74" />
            <stop offset="100%" stopColor="#ea580c" />
          </linearGradient>
          <linearGradient id="areaGrad2" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fb923c" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#fb923c" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

const ResearchAnimation = () => {
  const steps = [
    { icon: "🔍", label: "Skanowanie", source: "2,847 źródeł", delay: 0.2 },
    { icon: "🧬", label: "Analiza", source: "badania naukowe", delay: 0.8 },
    { icon: "📈", label: "Trend", source: "Twoja branża +340%", delay: 1.4 },
    { icon: "⏰", label: "Timing", source: "optymalny czas publikacji", delay: 2.0 },
    { icon: "✨", label: "Insight", source: "gotowy do użycia", delay: 2.6 },
  ];

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-0.5 overflow-hidden px-2">
      {/* Pipeline visualization */}
      {steps.map((step, i) => (
        <motion.div
          key={i}
          className="relative z-10 flex items-center gap-2 w-full"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: step.delay, duration: 0.5, ease: "easeOut" }}
        >
          {/* Step indicator */}
          <motion.div
            className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-orange-100 to-orange-200/80 flex items-center justify-center text-xs shadow-sm"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ delay: step.delay + 0.2, duration: 0.4 }}
          >
            {step.icon}
          </motion.div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-bold text-foreground/60 uppercase tracking-wider leading-none">
              {step.label}
            </div>
            <div className="text-[10px] md:text-[11px] text-foreground/80 font-medium leading-tight truncate">
              {step.source}
            </div>
          </div>

          {/* Progress indicator */}
          <motion.div
            className="flex-shrink-0"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: step.delay + 0.5, type: "spring", stiffness: 300 }}
          >
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-white" />
            </div>
          </motion.div>
        </motion.div>
      ))}

      {/* Connecting line */}
      <motion.div
        className="absolute left-[15px] top-[18%] bottom-[18%] w-[2px] rounded-full bg-gradient-to-b from-orange-300/0 via-orange-400/40 to-orange-300/0 z-0"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ delay: 0.3, duration: 2, ease: "easeOut" }}
        style={{ transformOrigin: "top" }}
      />

      {/* Scanning pulse */}
      <motion.div
        className="absolute inset-0 rounded-xl border-2 border-orange-400/0 z-20 pointer-events-none"
        animate={{ 
          borderColor: ["rgba(251,146,60,0)", "rgba(251,146,60,0.2)", "rgba(251,146,60,0)"],
        }}
        transition={{ duration: 2.5, repeat: Infinity, delay: 3 }}
      />
    </div>
  );
};

const BRAIN_OUTPUTS = ["Materiał", "Viral Score", "Hooki", "Ciekawostki"];

const BrainAnimation = () => {
  const sources = [
    { emoji: "📚", startX: -55, startY: -25 },
    { emoji: "📄", startX: -60, startY: 5 },
    { emoji: "📊", startX: -50, startY: 30 },
    { emoji: "📰", startX: -45, startY: -45 },
  ];

  const [outputIndex, setOutputIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setOutputIndex((prev) => (prev + 1) % BRAIN_OUTPUTS.length);
    }, 1400);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      {/* Sources flowing INTO brain from the left */}
      {sources.map((src, i) => (
        <motion.div
          key={`in-${i}`}
          className="absolute text-base"
          animate={{
            x: [src.startX, src.startX * 0.3, 0],
            y: [src.startY, src.startY * 0.3, 0],
            scale: [1, 0.7, 0],
            opacity: [0.85, 0.6, 0],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            delay: i * 0.45,
            ease: "easeIn",
          }}
        >
          {src.emoji}
        </motion.div>
      ))}

      {/* Brain — pulsing center */}
      <motion.div
        animate={{ scale: [1, 1.12, 1] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        className="relative z-10 text-5xl"
        style={{ filter: "drop-shadow(0 4px 12px rgba(234,88,12,0.25))" }}
      >
        🧠
      </motion.div>

      {/* Neural pulse rings */}
      {[0, 1].map((i) => (
        <motion.div
          key={`ring-${i}`}
          className="absolute rounded-full border border-orange-300/30"
          style={{ width: 80 + i * 30, height: 80 + i * 30 }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.1, 0.35] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
        />
      ))}

      {/* Output stream — cycles through 4 labels */}
      <AnimatePresence mode="wait">
        <motion.div
          key={outputIndex}
          initial={{ x: -15, opacity: 0, scale: 0.85 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          exit={{ x: 25, opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="absolute right-[5%] flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-300 shadow-sm"
        >
          <Sparkles className="w-3 h-3 text-emerald-600 flex-shrink-0" />
          <span className="text-[10px] font-bold text-emerald-700 whitespace-nowrap">
            {BRAIN_OUTPUTS[outputIndex]}
          </span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const FactCheckAnimation = () => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const DURATIONS = [1800, 2500, 2000];
    let timer: ReturnType<typeof setTimeout>;
    const advance = (current: number) => {
      timer = setTimeout(() => {
        const next = (current + 1) % 3;
        setPhase(next);
        advance(next);
      }, DURATIONS[current]);
    };
    advance(0);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative w-full h-full overflow-hidden">
      <AnimatePresence mode="wait">
        {phase === 0 && (
          <motion.div
            key="search"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3"
          >
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/85 border border-orange-100 shadow-sm w-full">
              <Search className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
              <div className="text-[11px] font-medium text-gray-800 truncate">
                Sprawdzam fakty
              </div>
            </div>
            <motion.div
              className="text-[9px] text-gray-500 font-medium tracking-wide"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              Skanuję publikacje i źródła...
            </motion.div>
          </motion.div>
        )}

        {phase === 1 && (
          <motion.div
            key="compare"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 flex flex-col gap-1.5 justify-center px-3"
          >
            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider">
              Porównuję źródła
            </div>
            {[
              { name: "Źródło", quality: 32, best: false, delay: 0.1 },
              { name: "Publikacja", quality: 96, best: true, delay: 0.25 },
              { name: "Literatura", quality: 28, best: false, delay: 0.4 },
            ].map((src) => (
              <motion.div
                key={src.name}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-2.5 py-1.5 border",
                  src.best
                    ? "bg-emerald-50 border-emerald-300 shadow-sm"
                    : "bg-white/60 border-orange-100"
                )}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: src.best ? 1 : 0.45, x: 0 }}
                transition={{ delay: src.delay, duration: 0.3 }}
              >
                <div
                  className={cn(
                    "text-[10px] font-semibold flex-1 truncate",
                    src.best ? "text-gray-800" : "text-gray-500"
                  )}
                >
                  {src.name}
                </div>
                <div
                  className={cn(
                    "text-[9px] font-bold tabular-nums",
                    src.best ? "text-emerald-600" : "text-gray-400"
                  )}
                >
                  {src.quality}%
                </div>
                {src.best && (
                  <motion.div
                    className="flex-shrink-0 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.0, type: "spring", stiffness: 300 }}
                  >
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {phase === 2 && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3"
          >
            <motion.div
              className="rounded-xl bg-emerald-50 border border-emerald-300 px-3 py-2.5 w-full shadow-sm"
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 220 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold text-gray-800 leading-tight">
                    Fakt zweryfikowany
                  </div>
                  <div className="text-[9px] text-emerald-600 leading-tight font-medium">
                    źródło: publikacja
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center gap-1.5 text-[10px] font-bold text-orange-600"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.3 }}
            >
              <ArrowDown className="w-3 h-3" />
              <span>Dodano do scenariusza</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TAB_ANIMATIONS = [
  InspirationAnimation,
  CreatorAnimation,
  LibraryAnimation,
  BrainAnimation,
  CompetitionAnimation,
  FactCheckAnimation,
];

/* ─── Features data ─── */

const features = [
  {
    icon: <Database className="w-5 h-5" />,
    title: "Magnes Pomysłów",
    subtitle: "Przyciągamy viralowe pomysły",
    tabEmoji: "🧲",
    points: [
      "Wybierasz twórców, którzy Cię inspirują.",
      "Analizujemy tematy, sprawdzamy co działa i ma potencjał.",
      "Podsuwamy Ci gotowy przepis na viralowe wideo.",
      "Kompletne materiały czekają na Ciebie w bibliotece.",
    ],
  },
  {
    icon: <Sparkles className="w-5 h-5" />,
    title: "Kreator Scenariuszy",
    subtitle: "Masz swój pomysł? Resztę bierzemy na siebie.",
    tabEmoji: "🎬",
    points: [
      "Ty piszesz jedno zdanie.",
      "Podpowiadamy Ci 5 wariantów, jak ograć ten temat.",
      "Wybierasz i klikasz. 60 sekund i gotowe.",
      "Dostajesz materiał szyty na miarę dla Ciebie i Twojego odbiorcy.",
    ],
  },
  {
    icon: <FileText className="w-5 h-5" />,
    title: "Biblioteka Pomysłów",
    subtitle: "Twoje kreatywne centrum dowodzenia",
    tabEmoji: "📚",
    points: [
      "Wszystkie pomysły w jednym miejscu.",
      "Sortujesz, oznaczasz, dodajesz do ulubionych.",
      "Zawsze wiesz, co nagrać jako następne.",
      "Pełny porządek. Zero chaosu.",
    ],
  },
  {
    icon: <BookOpen className="w-5 h-5" />,
    title: "Mózg Viraluj",
    subtitle: "Sekret każdego pomysłu",
    tabEmoji: "🧠",
    points: [
      "Czyta za Ciebie tysiące źródeł. Publikacje, badania, raporty.",
      "Myśli jak strateg, nie jak AI.",
      "Rozumie, jak działa uwaga człowieka.",
      "Zamienia myśl w gotowy materiał.",
    ],
  },
  {
    icon: <TrendingUp className="w-5 h-5" />,
    title: "Analityka i Konkurencja",
    subtitle: "Patrzysz, jak ich wyprzedzasz",
    tabEmoji: "📊",
    points: [
      "Masz na oku wszystkich rywali w jednym miejscu.",
      "Porównujesz swoje wyniki i reagujesz szybciej.",
      "Śledzisz swój wzrost na tle innych.",
      "Kontrolujesz swoją pozycję w wyścigu.",
    ],
  },
  {
    icon: <Check className="w-5 h-5" />,
    title: "Sprawdzanie faktów",
    subtitle: "Tylko potwierdzone informacje",
    tabEmoji: "✅",
    points: [
      "Weryfikujemy każdą informację.",
      "Wyjaśniamy znaczenie faktów i ciekawostek.",
      "Podajemy źródło informacji, które je potwierdza.",
      "Serwujemy wszystko w wygodnej i prostej formie.",
    ],
  },
];

// "Kreator Scenariuszy" is the strongest, most concrete tab — stays
// selected by default instead of auto-advancing away from it.
const DEFAULT_TAB_INDEX = 1;

const FeaturesGridSection = () => {
  const [activeIndex, setActiveIndex] = useState(DEFAULT_TAB_INDEX);
  const touchStartX = useRef<number | null>(null);

  const select = (i: number) => {
    setActiveIndex(i);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) select((activeIndex + 1) % features.length);
      else select((activeIndex - 1 + features.length) % features.length);
    }
    touchStartX.current = null;
  };

  const active = features[activeIndex];
  const ActiveAnimation = TAB_ANIMATIONS[activeIndex];

  return (
    <section id="features" className="relative py-20 md:py-28 overflow-hidden scroll-mt-24">
      {/* Brand blue background — HowItWorksSection's bottom bridge fades into
          this same blue directly above, so no top overlay is needed here. */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0080FF] to-[#006AD5]" />
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse 70% 50% at 60% 40%, rgba(255,255,255,0.08) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-5xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="mb-10 md:mb-14 md:text-left text-center"
        >
          <p className="text-sm font-semibold tracking-widest uppercase text-orange-300 mb-3">
            Funkcje
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter leading-[1.05] text-white mb-5">
            Jak to działa?
          </h2>
          <p className="text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl">
            Wszystko, czego potrzebujesz do tworzenia contentu w jednym miejscu.
          </p>
        </motion.div>

        <div
          className="rounded-3xl p-4 md:p-10"
          style={{
            background: "rgba(255, 255, 255, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.12)",
          }}
        >
          {/* Desktop tabs */}
          <div className="hidden md:grid grid-cols-3 gap-3 mb-8">
            {features.map((f, i) => (
              <button
                key={i}
                onClick={() => select(i)}
                className={`relative px-3 py-4 rounded-2xl font-bold transition-all duration-300 flex flex-col items-center gap-2 ${
                  i === activeIndex
                    ? "text-blue-600 scale-105"
                    : "text-white/75 hover:text-white hover:scale-[1.03]"
                }`}
                style={i !== activeIndex ? {
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.08)",
                } : undefined}
              >
                {i === activeIndex && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 rounded-2xl bg-white"
                    style={{
                      border: "1.5px solid rgba(37,99,235,0.25)",
                      boxShadow: "0 8px 24px rgba(37,99,235,0.12), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.03)",
                    }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10 text-2xl">{f.tabEmoji}</span>
                <span className="relative z-10 text-[13px] leading-tight text-center">{f.title}</span>
              </button>
            ))}
          </div>

          {/* Mobile tabs - 3x2 grid */}
          <div className="md:hidden grid grid-cols-3 gap-2 mb-5">
            {features.map((f, i) => (
              <button
                key={i}
                onClick={() => select(i)}
                className={`relative px-2 py-3 rounded-2xl text-xs font-bold transition-all duration-300 flex flex-col items-center gap-1.5 ${
                  i === activeIndex
                    ? "text-blue-600 scale-105"
                    : "text-white/75"
                }`}
                style={i !== activeIndex ? {
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.08)",
                } : undefined}
              >
                {i === activeIndex && (
                  <motion.div
                    layoutId="activeTabMobile"
                    className="absolute inset-0 rounded-2xl bg-white"
                    style={{
                      border: "1.5px solid rgba(37,99,235,0.25)",
                      boxShadow: "0 8px 24px rgba(37,99,235,0.12), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.03)",
                    }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10 text-lg">{f.tabEmoji}</span>
                <span className="relative z-10 text-[10px] leading-tight text-center">{f.title}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div
            className="relative min-h-[340px] md:min-h-[280px]"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.97 }}
                transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                className="rounded-2xl bg-white/80 border border-white/60 p-6 md:p-8 shadow-sm"
                style={{ backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
              >
                <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                  {/* Left: Animation + title */}
                  <div className="md:w-2/5 flex flex-col items-center">
                    {/* Animated illustration */}
                    <div className="w-56 h-36 md:w-80 md:h-44 mb-4 relative">
                      <div
                        className="absolute inset-0 rounded-2xl"
                        style={{
                          background: "radial-gradient(circle at center, rgba(251,146,60,0.08) 0%, transparent 70%)",
                        }}
                      />
                      <ActiveAnimation />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold tracking-tight leading-tight text-gray-900 mb-1 text-center">
                      {active.title}
                    </h3>
                    <p className="text-sm text-gray-500 text-center">{active.subtitle}</p>
                  </div>

                  {/* Right: Points */}
                  <div className="md:w-3/5 space-y-3 flex flex-col justify-center">
                    {active.points.map((point, pi) => (
                      <motion.div
                        key={pi}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: pi * 0.08, duration: 0.3 }}
                        className="flex items-start gap-3"
                      >
                        <div className="mt-0.5 w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-orange-600" />
                        </div>
                        <p className="text-gray-700 text-base leading-relaxed">{point}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Mobile dots */}
          <div className="flex md:hidden justify-center gap-2 mt-5">
            {features.map((_, i) => (
              <button
                key={i}
                onClick={() => select(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeIndex ? "w-6 bg-orange-400" : "w-1.5 bg-white/25"
                }`}
                aria-label={`Feature ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesGridSection;
